import React, { useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Info, AlertTriangle } from "lucide-react";

const ConsumerInformationSection = ({ data, onChange, brokerData }) => {
  const handleCheckboxChange = (field, checked) => {
    onChange({ [field]: checked });
  };

  // Auto-generează data curentă
  const getCurrentDate = () => {
    const today = new Date();
    const day = today.getDate().toString().padStart(2, '0');
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const year = today.getFullYear();
    return `${day}.${month}.${year}`;
  };

  // Extrage datele brokerului - calculat în render pentru a reflecta update-urile
  const getCompanyName = () => brokerData?.companyName || brokerData?.companyData?.companyName || "";
  const getRepresentativeName = () => brokerData?.representativeName || 
    (brokerData?.representativeData ? 
      `${brokerData.representativeData.firstName || ""} ${brokerData.representativeData.lastName || ""}`.trim() : "");
  const getCui = () => brokerData?.cui || brokerData?.companyData?.cui || "";
  const getJNumber = () => brokerData?.jNumber || brokerData?.companyData?.jNumber || "";
  const getAddress = () => brokerData?.address || brokerData?.companyData?.address || "";
  // Extrage orașul din adresă (prima parte înainte de virgulă) sau folosește câmpul city dacă există
  const getCity = () => {
    if (brokerData?.city || brokerData?.companyData?.city) {
      return brokerData?.city || brokerData?.companyData?.city;
    }
    // Încearcă să extragă orașul din adresă (ex: "București, Str. X" -> "București")
    const address = brokerData?.address || brokerData?.companyData?.address || "";
    if (address.includes(',')) {
      return address.split(',')[0].trim();
    }
    return "";
  };
  const getEmail = () => brokerData?.email || "";
  const getWebsite = () => brokerData?.website || brokerData?.companyData?.website || "";
  const getWorkLocation = () => brokerData?.workLocation || brokerData?.companyData?.workLocation || "";

  return (
    <div className="space-y-6">
      {/* Informare Consumator OUG 52 */}
      <Card className="border-blue-200 bg-blue-50/30">
        <CardHeader className="bg-gradient-to-r from-blue-100 to-indigo-100 border-b border-blue-200">
          <CardTitle className="flex items-center gap-2 text-blue-900">
            <Info className="h-6 w-6" />
            Informare Consumator conform OUG 52/2016
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="bg-white p-6 rounded-lg border border-blue-200">
            <div className="text-sm text-gray-800 leading-relaxed space-y-4">
              <p className="font-semibold text-lg text-center text-blue-900 mb-6">
                Informare consumator
              </p>
              
              <p>
                <strong>Având în vedere prevederile OUG nr. 52/2016 privind contractele de credit oferite 
                consumatorilor pentru bunuri imobile, cu modificările și completările ulterioare, vă informăm:</strong>
              </p>

              <div className="ml-4 space-y-3">
                <div className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <div>
                    <p>
                      <strong>SC {getCompanyName() || "[NUME FIRMA BROKER]"}</strong> cu sediul social în {getAddress() || "[ADRESĂ COMPLETĂ]"}, 
                      înregistrată la Oficiul Registrului Comerțului sub numărul {getJNumber() || "[J40/XXXXX]"}, 
                      având CUI {getCui() || "[CUI BROKER]"}:
                    </p>
                    <div className="ml-4 mt-2 space-y-1">
                      <p className="flex items-center gap-2">
                        <span className="text-green-600">✓</span>
                        este înscris în Registrul de la A.N.P.C., https://anpc.ro/intermediari-credite/
                      </p>
                      <p className="flex items-center gap-2">
                        <span className="text-green-600">✓</span>
                        activează în calitate de intermediar de credite nelegat
                      </p>
                      <p className="flex items-center gap-2">
                        <span className="text-green-600">✓</span>
                        nu prestează servicii de consiliere
                      </p>
                      <p className="flex items-center gap-2">
                        <span className="text-green-600">✓</span>
                        nu percepe comision de la consumator pentru serviciile de intermediere
                      </p>
                      <p className="flex items-center gap-2">
                        <span className="text-green-600">✓</span>
                        este remunerat de către creditori pentru activitatea de intermediere
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <p>
                    având în vedere că nu se cunoaște cu exactitate valoarea comisionului/stimulentului încasat 
                    de către SC {getCompanyName() || "[NUME FIRMA BROKER]"} în legătură cu contractul de credit, suma exactă va fi 
                    furnizată în FEIS „Fișa Europeană de Informații Standardizate" de către creditor;
                  </p>
                </div>

                <div className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <p>
                    Conform art. 99 alin. 4 din OUG 52/20.09.2016 vă informăm că la cerere, vă putem furniza 
                    informații despre variațiile nivelului comisioanelor/stimulentelor plătibile de diferiți 
                    creditori parteneri;
                  </p>
                </div>

                <div className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <p>
                    lista creditorilor colaboratori este actualizată constant și este disponibilă online pe site-ul 
                    companiei, la secțiunea Parteneri – {getWebsite() || "[WEBSITE BROKER]"};
                  </p>
                </div>

                <div className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <p>
                    Vă aducem la cunoștință că potrivit art. 10 alin. 1, lit. f din OUG 52/20.09.2016 reclamațiile 
                    la nivel intern pot fi transmise la adresa de email din antet sau la sediul societății. 
                    {" "}{getCompanyName() || "[NUME FIRMA BROKER]"} va comunica răspunsul în termenul legal aplicabil. Consumatorul 
                    se poate adresa și Autorității Naționale pentru Protecția Consumatorilor și, după caz, 
                    mecanismelor de soluționare alternativă a litigiilor. Ca urmare a reclamației, veți primi un 
                    răspuns în termenul legal prevăzut de lege;
                  </p>
                </div>

                <div className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <p>
                    conform art. 70 alin. 2 din OUG 52/20.09.2016 consumatorul trebuie să furnizeze informații 
                    corecte și complete pentru evaluarea corespunzătoare a bonității și pentru identificarea 
                    produselor de credit adecvate situației declarate;
                  </p>
                </div>

                <div className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <p>
                    conform art. 70 alin. 3 din OUG 52/20.09.2016 vă avertizăm că în cazul în care creditorul nu 
                    poate efectua evaluarea corespunzătoare a bonității sau să întreprindă verificările necesare 
                    deoarece consumatorul decide să nu furnizeze informațiile, creditul nu poate fi acordat;
                  </p>
                </div>

                <div className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <p>
                    vă aducem la cunoștință că potrivit art. 10 alin. 1 din OUG 52/20.09.2016, intermediarul de 
                    credite sau reprezentantul desemnat are obligația de a vă furniza toate informațiile de mai sus 
                    în timp util, dar nu mai puțin de <strong>5 zile calendaristice</strong>, înainte de desfășurarea 
                    oricăreia din activitățile de intermediere;
                  </p>
                </div>

                <div className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <p>
                    aveți la dispoziție un termen de <strong>5 zile calendaristice</strong> pentru a decide dacă doriți să beneficiați 
                    de serviciile noastre;
                  </p>
                </div>

                <div className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <p>
                    puteți opta pentru <strong>reducerea perioadei de 5 zile calendaristice</strong> precizate mai sus;
                  </p>
                </div>

                <div className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <p>
                    <strong>înainte de acceptarea unei oferte sau semnarea unui contract de credit</strong>, beneficiați de 
                    perioada de reflecție de <strong>15 zile calendaristice</strong> prevăzută de lege și de informațiile 
                    precontractuale aplicabile produsului de credit;
                  </p>
                </div>

                <div className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <p>
                    <strong>serviciile de intermediere</strong> și principalele drepturi și obligații ale părților sunt 
                    detaliate, după caz, în documentul Condiții Generale de Afaceri „CGA".
                  </p>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-300">
                <p className="text-center text-sm">
                  Prezentul act a fost semnat astăzi, {getCurrentDate()}, în 2 (două) exemplare, 
                  câte un exemplar pentru fiecare parte.
                </p>
                
                <div className="flex justify-between mt-8">
                  <div className="text-center">
                    <p className="font-semibold">SC {getCompanyName() || "[NUME FIRMA BROKER]"}</p>
                    <p>{getRepresentativeName() || "[NUME REPREZENTANT]"}</p>
                  </div>
                  <div className="text-center">
                    <p className="font-semibold">Consumator</p>
                    <p>_____________________</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bife obligatorii pentru informare */}
          <div className="space-y-4 mt-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
                <p className="font-semibold text-yellow-800">Confirmări obligatorii:</p>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-start space-x-2">
                  <Checkbox 
                    id="acceptInformation"
                    checked={data.acceptInformation || false}
                    onCheckedChange={(checked) => handleCheckboxChange('acceptInformation', checked)}
                    className="mt-1"
                  />
                  <Label htmlFor="acceptInformation" className="text-sm leading-relaxed">
                    <strong>Confirm</strong> că am primit și am citit informarea consumator conform OUG 52/2016 
                    și înțeleg condițiile de colaborare cu intermediarul de credite.
                  </Label>
                </div>

                <div className="flex items-start space-x-2">
                  <Checkbox 
                    id="acceptDataProcessing"
                    checked={data.acceptDataProcessing || false}
                    onCheckedChange={(checked) => handleCheckboxChange('acceptDataProcessing', checked)}
                    className="mt-1"
                  />
                  <Label htmlFor="acceptDataProcessing" className="text-sm leading-relaxed">
                    <strong>Sunt de acord</strong> cu prelucrarea datelor cu caracter personal pentru intermedierea 
                    contractelor de credit și transmiterea către băncile partenere pentru obținerea de oferte personalizate.
                  </Label>
                </div>

                <div className="flex items-start space-x-2">
                  <Checkbox 
                    id="acceptTerms"
                    checked={data.acceptTerms || false}
                    onCheckedChange={(checked) => handleCheckboxChange('acceptTerms', checked)}
                    className="mt-1"
                  />
                  <Label htmlFor="acceptTerms" className="text-sm leading-relaxed">
                    <strong>Înțeleg</strong> că decizia de aprobare sau respingere a cererii de credit aparține 
                    în exclusivitate creditorului și că intermediarul nu garantează aprobarea creditului.
                  </Label>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConsumerInformationSection;